import { useMemo } from "react";

export type RendererMode = "gpu" | "cpu";
export type FallbackReason =
  | "none"
  | "webgl_unavailable"
  | "offscreen_unavailable"
  | "gpu_disabled_by_user";

export interface RendererModeResult {
  mode: RendererMode;
  fallbackReason: FallbackReason;
  webgl2Supported: boolean;
  offscreenCanvasSupported: boolean;
}

let cachedWebGL2Support: boolean | null = null;

function detectWebGL2Support(): boolean {
  if (typeof document === "undefined") return false;
  if (cachedWebGL2Support !== null) return cachedWebGL2Support;

  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2") as
      | (WebGL2RenderingContext & {
          getExtension(name: "WEBGL_lose_context"):
            | { loseContext(): void }
            | null;
        })
      | null;
    cachedWebGL2Support = !!gl;
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
    canvas.width = 1;
    canvas.height = 1;
    return cachedWebGL2Support;
  } catch {
    cachedWebGL2Support = false;
    return false;
  }
}

function detectOffscreenCanvasSupport(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof (window as any).OffscreenCanvas !== "undefined"
  );
}

export default function useRendererMode(
  preference: "gpu-preferred" | "cpu-only" = "gpu-preferred",
): RendererModeResult {
  return useMemo(() => {
    const webgl2Supported = detectWebGL2Support();
    const offscreenCanvasSupported = detectOffscreenCanvasSupport();

    if (preference === "cpu-only") {
      return {
        mode: "cpu",
        fallbackReason: "gpu_disabled_by_user",
        webgl2Supported,
        offscreenCanvasSupported,
      };
    }

    if (!webgl2Supported) {
      return {
        mode: "cpu",
        fallbackReason: "webgl_unavailable",
        webgl2Supported,
        offscreenCanvasSupported,
      };
    }

    // OffscreenCanvas is NOT required for the EEGWaveformRenderer GPU path
    // (it creates a regular off-screen HTMLCanvasElement).  Keep the detection
    // result as a diagnostic field but do not gate the "gpu" mode on it.
    return {
      mode: "gpu",
      fallbackReason: "none",
      webgl2Supported,
      offscreenCanvasSupported,
    };
  }, [preference]);
}
